import { Button } from './ui/Primitives';
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const money = cents => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
const date = seconds => new Date(seconds * 1000).toLocaleString();
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwt_authorization')}` } });

export default function BillingLifecycle() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState('solo');
  const [busy, setBusy] = useState(false);
  const dialog = useRef(null);
  useEffect(() => {
    let mounted = true;
    const refresh = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_HOST}/v2/billing/lifecycle`, auth());
        if (mounted) { setData(response.data); setError(''); }
      } catch (err) { if (mounted) setError('Unable to check account recovery status. Please refresh shortly.'); }
    };
    refresh();
    const timer = setInterval(refresh, 60000);
    return () => { mounted = false; clearInterval(timer); };
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.activeElement;
    dialog.current?.focus();
    return () => previous?.focus();
  }, [open]);
  const lifecycle = data?.lifecycle;
  async function recover(event) {
    event.preventDefault(); setBusy(true); setError('');
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_HOST}/v2/billing/recover`, { plan }, auth());
      if (response.data.url) window.location.assign(response.data.url);
      else window.location.reload();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to start checkout. Please try again.'); setBusy(false);
    }
  }
  function onKeyDown(event) {
    if (event.key === 'Escape' && !busy) setOpen(false);
    if (event.key !== 'Tab') return;
    const items = Array.from(dialog.current.querySelectorAll('button:not(:disabled), input:not(:disabled)'));
    const first = items[0]; const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  }
  return <div className="phone-billing-lifecycle">
    {lifecycle?.state === 'cancel_scheduled' && <p role="status">Your cancellation is scheduled. Service continues until {date(lifecycle.service_ends_at)}. Use <Link to="/account#account-billing-title">Account settings → Manage subscription</Link> to undo cancellation.</p>}
    {lifecycle?.state === 'payment_required' && <p role="status">Your AI receptionist is paused. Use <Link to="/account#account-billing-title">Account settings → Manage subscription</Link> to update your payment method and pay any outstanding invoice. Adding a card alone does not restore service.</p>}
    {lifecycle?.state === 'retention' && <><p role="status">Your service has ended. Reactivate before {date(lifecycle.delete_after)} to keep your account data and phone number. Removal is scheduled after that date.</p><Button variant="secondary" size="sm" onClick={() => setOpen(true)}>Reactivate subscription</Button></>}
    {lifecycle?.state === 'review' && <p role="status">Your service is paused and the account needs a billing review. Please contact support.</p>}
    {['deleting', 'deleted'].includes(lifecycle?.state) && <p role="status">Account removal has started. Self-service reactivation is no longer available. Stripe billing records are retained.</p>}
    {error && !open && <p role="alert">{error}</p>}
    {open && <div className="phone-plan-modal-backdrop"><section className="phone-plan-modal" role="dialog" aria-modal="true" aria-labelledby="billing-recovery-title" aria-describedby="billing-recovery-description" tabIndex={-1} ref={dialog} onKeyDown={onKeyDown}>
      <h2 id="billing-recovery-title">Reactivate your receptionist</h2>
      <p id="billing-recovery-description">Choose a monthly subscription. Payment starts a new billing period immediately. There is no additional free trial. Stripe will show the total before you pay.</p>
      <form onSubmit={recover}><div className="phone-plan-options">{data?.plans.map(option => <label className={`phone-plan-option${plan === option.id ? ' selected' : ''}`} key={option.id}><input type="radio" name="recovery-plan" value={option.id} checked={plan === option.id} disabled={busy} onChange={event => setPlan(event.target.value)} /><span><strong>{option.name}</strong><small>{option.included_minutes} AI minutes · {money(option.overage_cents_per_minute)}/additional minute</small></span><b>{money(option.monthly_cents)}<small>USD / month</small></b></label>)}</div>
        {error && <p role="alert">{error}</p>}
        <div className="phone-plan-modal-actions"><Button type="button" variant="secondary" disabled={busy} onClick={() => setOpen(false)}>Cancel</Button><Button type="submit" disabled={busy} loading={busy} loadingLabel={"Opening Stripe…"}>{'Continue to Stripe'}</Button></div>
      </form>
    </section></div>}
  </div>;
}
